import React, { useState, useEffect, Fragment } from "react";
import { Card, StyledBody, StyledAction, StyledHeaderImage } from "baseui/card";
import { Button, SIZE as BSIZE, KIND } from "baseui/button";
import { ButtonGroup } from "baseui/button-group"; // Import ButtonGroup
import ItemModal from "../item-modal/ItemModalComponent";
import { RESOURCE_TYPES } from "../../constants/AppEnums";

const MAX_TEXT_LENGTH = 20;
const MAX_TITLE_LENGTH = 12; // Adjust as needed

const truncateText = (text, maxLength) =>
  text?.length > maxLength ? text.substring(0, maxLength - 3) + "..." : text;

export default function ItemCard({
  item,
  resourceType,
  onSelectClick,
  editable,
  onCreateClick,
  onBuyClick,
  itemIsLoading,
}) {
  // Initialize imageSrc state with the path to the empty image
  const EMPTY_IMAGE_PATH = require("../../../images/empty.jpg");
  const [imageSrc, setImageSrc] = useState(EMPTY_IMAGE_PATH);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const openModal = () => setIsModalOpen(true);
  const closeModal = () => setIsModalOpen(false);

  useEffect(() => {
    if (item.thumbnailImageUrl) setImageSrc(item.thumbnailImageUrl);
  }, [item.id, item.thumbnailImage]);

  return (
    <Fragment>
      <Card
        overrides={{
          Root: {
            style: ({ $theme }) => ({
              minWidth: "250px",
              maxWidth: "250px",
              height: "380px",
              borderRadius: "15px",
              padding: "10px",
              margin: "5px 5px",
              cursor: "pointer",
              transition: "transform 0.3s ease, box-shadow 0.3s ease", // Smooth transitions
              ":hover": {
                transform: "translateY(-5px)", // Slightly move the card up
                boxShadow: `${$theme.lighting.shadow500}`, // Increase shadow for "lift" effect
              },
            }),
          },
        }}
        // headerImage={imageSrc}
        title={truncateText(item.title, MAX_TITLE_LENGTH)}
      >
        <StyledHeaderImage
          onClick={openModal}
          $style={{
            minBlockSize: "150px", // Minimum height for the image
            height: "auto", // This ensures the image's height adjusts to the content
            maxHeight: "150px", // Maximum height for the image
            width: "100%", // This will ensure the image width aligns with the container width
            objectFit: "contain", // This ensures the image fits within the given dimensions without stretching
            display: "block", // Ensures the image does not inline with other elements
            overflow: "hidden", // Prevents the image from exceeding the card boundaries
            borderRadius: "8px", // Optional, if you want rounded corners
          }}
          src={imageSrc}
        />
        <StyledBody onClick={openModal}>
          {truncateText(item.description, MAX_TEXT_LENGTH)}
        </StyledBody>
        <StyledBody
          onClick={openModal}
          $style={{
            fontSize: "12px",
            marginTop: "5px",
            color: "#888", // a subtle color to differentiate from main content
          }}
        >
          {resourceType === RESOURCE_TYPES.PRODUCT
            ? `Price:  $ ${item.price}`
            : `Created by: ${item.creator || "deleted"}`}
          {resourceType === RESOURCE_TYPES.PRODUCT ? (
            <div></div>
          ) : (
            <div>{`${item.timeSinceCreated || "unknown"}`}</div>
          )}
        </StyledBody>
        <StyledAction $style={{ display: "flex", justifyContent: "center" }}>
          {resourceType === RESOURCE_TYPES.PRODUCT ? (
            <Button
              shape="pill"
              size={BSIZE.mini}
              onClick={() => onBuyClick()}
              overrides={{ BaseButton: { style: { width: "100%" } } }}
              isLoading={itemIsLoading}
              disabled={itemIsLoading}
            >
              Buy
            </Button>
          ) : (
            <ButtonGroup size="compact" kind={KIND.primary}>
              <Button
                shape="pill"
                size={BSIZE.mini}
                onClick={() => onCreateClick()}
                overrides={{ BaseButton: { style: { marginRight: "5px" } } }}
              >
                Clone
              </Button>
              <Button
                shape="pill"
                size={BSIZE.mini}
                disabled={!editable}
                onClick={() => onSelectClick()}
              >
                Select
              </Button>
            </ButtonGroup>
          )}
        </StyledAction>
      </Card>
      <ItemModal
        item={item}
        resourceType={resourceType}
        imageSrc={imageSrc}
        isOpen={isModalOpen}
        onClose={closeModal}
      />
    </Fragment>
  );
}
